import { Box, Button, Chip, IconButton, Typography, useTheme } from '@mui/material';
import ExoForm from '../exo/ExoForm';
import ExoDialog from '../exo/ExoDialog';
import { useTranslation } from 'react-i18next';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import { useEffect, useState } from 'react';
import { tokens } from '../../global/theme/tokens';
import {
  API_DELETE,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../api/api';
import * as Yup from 'yup';
import { TypeSmallIconCards } from '../form/form-fields/TypeSmallIconCards';
import DeleteIcon from '@mui/icons-material/Delete';
import SelectService from '../select/SelectService';

export const NewBillingPosition = ({
  onClose = () => {},
  onCreate,
  onUpdate,
  onDelete,
  startData,
  projectId,
}) => {
  const { t } = useTranslation();
  const config = useConfig();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const [isLoading, setIsLoading] = useState(false);
  const [startDataCollection, setStartDataCollection] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [showServiceSelector, setShowServiceSelector] = useState(false);

  useEffect(() => {
    setStartDataCollection(
      startData ? { ...startData, projectId } : { projectId, taxRate: 19 }
    );
    if (startData) {
      setPositionType(startData.type);
      // Load service if present
      if (startData.serviceId && startData.service) {
        setSelectedService(startData.service);
      } else {
        setSelectedService(null);
      }
    } else {
      setSelectedService(null);
    }
  }, [startData, projectId]);

  // different Types
  const [positionType, setPositionType] = useState('time');

  const fieldsTime = [
    {
      label: t('Name'),
      type: 'text',
      key: 'name',
      colspan: 3,
    },
    {
      label: t('Tax Rate'),
      type: 'number',
      colspan: 1,
      key: 'taxRate',
      unit: '%',
    },
    {
      label: t('Price'),
      type: 'money',
      colspan: 1,
      key: 'price',
    },

    {
      label: t('Image'),
      type: 'image',
      key: 'image',
    },
    {
      label: t('Description'),
      type: 'description',
      colspan: 3,
      key: 'description',
    },
  ];
  const validationSchemaTime = Yup.object().shape({
    name: Yup.string().required(t('Is required')),
    price: Yup.string().required(t('Is required')),
    taxRate: Yup.string().required(t('Is required')),
    projectId: Yup.string().required(t('Is required')),
  });

  const fieldsFlat = [
    {
      label: t('Name'),
      type: 'text',
      key: 'name',
      colspan: 4,
    },
    {
      label: t('Amount'),
      type: 'number',
      colspan: 1,
      key: 'amount',
    },
    {
      label: t('Unit'),
      type: 'text',
      colspan: 1,
      key: 'unit',
    },
    {
      label: t('Tax Rate'),
      type: 'number',
      colspan: 1,
      key: 'taxRate',
      unit: '%',
    },
    {
      label: t('Price'),
      type: 'money',
      colspan: 1,
      key: 'price',
    },

    {
      label: t('Image'),
      type: 'image',
      key: 'image',
    },
    {
      label: t('Description'),
      type: 'description',
      colspan: 4,
      key: 'description',
    },
  ];
  const validationSchemaFlat = Yup.object().shape({
    name: Yup.string().required(t('Is required')),
    price: Yup.string().required(t('Is required')),
    amount: Yup.string().required(t('Is required')),
    taxRate: Yup.string().required(t('Is required')),
    projectId: Yup.string().required(t('Is required')),
  });

  const formTypeMap = {
    time: {
      fields: fieldsTime,
      validationSchema: validationSchemaTime,
      cols: 3,
    },
    flat: {
      fields: fieldsFlat,
      validationSchema: validationSchemaFlat,
      cols: 4,
    },
  };

  function handleServiceSelect(service) {
    setSelectedService(service);
    setStartDataCollection((prev) => ({
      ...prev,
      name: service.name,
      description: service.description,
      unit: service.unit,
      price: service.price,
      taxRate: service.taxRate,
      image: service.image,
      serviceId: service.id,
    }));
    setPositionType('time');
    setShowServiceSelector(false);
  }

  function handleClearService() {
    setSelectedService(null);
    setStartDataCollection((prev) => ({
      ...prev,
      serviceId: null,
    }));
  }

  function handleSubmit(data) {
    const isUpdate = Boolean(data?.id);

    const reqData = {
      ...data,
      type: positionType,
      serviceId: data.serviceId ?? selectedService?.id ?? null,
    };

    DBRequest({
      config,
      path: isUpdate ? `project-positions/${data.id}` : `project-positions`,
      method: isUpdate ? API_PATCH(reqData) : API_POST(reqData),
      onResponse: isUpdate ? onUpdate : onCreate,
      onLoading: setIsLoading,
    });
  }

  function handleDelete() {
    DBRequest({
      config,
      path: `project-positions/${startData.id}`,
      method: API_DELETE(),
      onResponse: () => {
        onDelete(startData);
        onClose();
      },
    });
  }
  const { fields, validationSchema, cols } = formTypeMap[positionType];

  return (
    <Box className="relative">
      {startData?.id && (
        <IconButton
          onClick={handleDelete}
          color="error"
          className="top-0 right-0"
          sx={{ position: 'absolute' }}
        >
          <DeleteIcon />
        </IconButton>
      )}
      <Typography variant="h3" textAlign="center" className="  py-2">
        {startData?.id
          ? t('Update billable position')
          : t('New billable position')}
      </Typography>
      <Box className="flex gap-2 mb-4 px-3 items-center flex-wrap">
        <Button
          variant="text"
          onClick={() => setShowServiceSelector(true)}
          startIcon={<SupervisedUserCircleIcon />}
          size="small"
          sx={{
            color: colors.text,
            borderRadius: '8px',
            textTransform: 'none',
            '&:hover': {
              bgcolor: colors.selected,
            },
          }}
        >
          {t('Load from Service')}
        </Button>
        {selectedService && (
          <Chip
            label={`${selectedService.nr} - ${selectedService.name}`}
            onDelete={handleClearService}
            size="small"
            sx={{
              bgcolor: colors.info + '20',
              color: colors.info,
              border: 'none',
              '& .MuiChip-deleteIcon': {
                color: colors.info,
                '&:hover': {
                  color: colors.text,
                },
              },
            }}
          />
        )}
      </Box>
      <TypeSmallIconCards
        field={{
          label: t('What') + '?',
          key: 'type',
          type: 'smallIconCards',
          colspan: 3,
          cards: [
            {
              label: t('Worktime'),
              Icon: AccessTimeIcon,
              value: 'time',
            },
            {
              label: t('Pauschal'),
              Icon: AttachMoneyIcon,
              value: 'flat',
            },
          ],
        }}
        dataCollection={{ type: positionType }}
        updateValidationOnChange={(e, type) => setPositionType(type)}
      />
      <ExoForm
        isLoading={isLoading}
        fields={fields}
        startDataCollection={startDataCollection}
        onSubmit={handleSubmit}
        onCancel={onClose}
        validationSchema={validationSchema}
        className={`grid gap-4 grid-cols- max-h-full overflow-y-auto pt-4 px-3 overflow-x-hidden`}
        sx={{
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        }}
        noDifferences
      />
      <ExoDialog
        open={showServiceSelector}
        onClose={() => setShowServiceSelector(false)}
      >
        <SelectService
          onSelect={handleServiceSelect}
          onClose={() => setShowServiceSelector(false)}
        />
      </ExoDialog>
    </Box>
  );
};
